import * as ngCore from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as models from '../models'

import * as overlay from './overlay'
import { AtnDsSelectSingleStore } from './store'

/**
 * Basic single select input.
 *
 * **Inputs**
 * - {@link Component._items items}!
 * - {@link Component._selected selected}?
 * - {@link Component._loading loading}?
 * - {@link Component._label label}?
 * - {@link Component._config config}?
 * - {@link Component._disabled disabled}?
 *
 * **Outputs**
 * - {@link Component.change change}
 *
 * @example
 * ```html
 * <atn-ds-select-single
 *  [items]="items"
 *  [selected]="selected"
 *  [loading]="false"
 *  [label]="'Select Input'"
 *  [config]="config"
 *  [disabled]="false"
 *  (change)="handleChange($event)"
 * />
 * ```
 */
@ngCore.Component({
  imports: [overlay.Component],
  providers: [AtnDsSelectSingleStore],
  selector: 'atn-ds-select-single',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<TValue> {
  /** @see {@link AtnDsSelectSingleStore} */
  public store = ngCore.inject(AtnDsSelectSingleStore)
  /** TODO:  */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _itemsSg = ngCore.input<readonly models.Item<TValue>[]>([], {
    alias: 'items',
  })
  /** TODO: */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _loadingSg = ngCore.input(false, { alias: 'loading' })
  /** TODO: */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })
  /**
   * Whether the select is required or not.
   */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _requiredSg = ngCore.input(false, { alias: 'required' })

  /**
   * Placeholder to display when no item is selected.
   */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _placeholderSg = ngCore.input<string | undefined>(undefined, {
    alias: 'placeholder',
  })

  /** @see {@link models.Config }*/
  // NOTE: public only for the purpose of ngCore.input
  public readonly _configSg = ngCore.input<models.Config | undefined>(
    undefined,
    {
      alias: 'config',
    },
  )
  /** TODO:  */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _errorSg = ngCore.input<string | Error | undefined>(
    undefined,
    {
      alias: 'error',
    },
  )
  /** TODO:  */
  // NOTE: public only for the purpose of ngCore.input
  public readonly _disabledSg = ngCore.input(false, { alias: 'disabled' })

  /**
   * @default undefined
   */
  public readonly valueSg = ngCore.model<TValue | undefined>(undefined, {
    alias: 'value',
  })

  /**
   * Emits an event when the "refresh" arrow on the error banner is clicked,
   * while in the {@link errorSg} state.
   *
   * __NOTE__: Error button will be present only if this handler is defined
   */
  // Can't use 'ngCore.output', because it doesn't have 'observed' property
  @ngCore.Output()
  public readonly refetch = new ngCore.EventEmitter<void>()

  constructor() {
    ngCore.effect(() => {
      const items = this._itemsSg()
      const required = this._requiredSg()
      const placeholder = this._placeholderSg()

      this.store.patchState({
        // If items are required, we don't need to add a placeholder item.
        items: required
          ? items
          : // If items are not required, we add a placeholder item to the
            // beginning of the list. (or 'None' if no placeholder is given)
            [
              // If we have items, add a placeholder item to the beginning of the
              // list.
              ...(items.length > 0
                ? [{ label: placeholder ?? 'None', value: undefined }]
                : []),
              ...items,
            ],
      })
    })
    ngCore.effect(() => {
      const placeholder = this._placeholderSg()
      this.store.patchState({ placeholder })
    })
    ngCore.effect(() => {
      const selectedValue = this.valueSg()
      this.store.patchState({ selectedValue })
    })
    ngCore.effect(() => {
      const loading = this._loadingSg()
      this.store.patchState({ loading })
    })
    ngCore.effect(() => {
      const label = this._labelSg()
      this.store.patchState({ label })
    })
    ngCore.effect(() => {
      const config = this._configSg()

      // TODO: this was just copy pasted, evaluate the possibility of using
      // Object.assign.
      this.store.patchState(state => ({
        ...state,
        config: {
          ...state.config,
          ...config,
          overlay: {
            ...state.config.overlay,
            ...config?.overlay,
          },
        },
      }))
    })
    ngCore.effect(() => {
      const error = this._errorSg()
      this.store.patchState({ error })
    })
    ngCore.effect(() => {
      const disabled = this._disabledSg()
      this.store.patchState({ disabled })
    })
    ngCore.effect(() => {
      const required = this._requiredSg()
      this.store.patchState({ required })
    })
    /**
     * Readback effect: syncs the store's selected value back to the
     * host-bound `valueSg` model signal so Angular can emit
     * `(valueChange)`.  We guard against no-op writes because the
     * store initialises with `undefined` *before* the inbound value
     * effect has run.  Without this check every reference-type input
     * (e.g. Room → Room Type) would emit an erroneous `undefined`
     * change on first render, causing a second editing-plugin `edit`
     * dispatch that clears the field.
     */
    ngCore.effect(() => {
      const value = this.store.selectedValueSg()
      if (value !== this.valueSg()) {
        // FIXME: Find a way to get correct type for the value
        this.valueSg.set(value as TValue | undefined)
      }
    })
  }
}
